import { useEffect, useEffectEvent, useState } from 'react';
import { seedColRange, visibleColRange, type IColRange } from './visibleCols';
import { seedRowRange, visibleRowRange, type IRowGeometry } from './visibleRows';

export interface IGridWindow {
  cols: IColRange;
  rows: IColRange;
}

/**
 * Track the visible COLUMN and ROW range of the tracker's scroller with ONE
 * subscription for both axes — one scroll listener computes both ranges per
 * frame, rather than paying for two independent hooks' worth of listeners and
 * rAF coalescing (and, more importantly, two independent state slices that
 * would each trigger their own commit on every frame that touches either axis).
 *
 * Starts from a bounded SEED window (`seedColRange` / `seedRowRange`) rather
 * than null: the scroller element cannot exist before the first commit, and a
 * null ("render everything") first commit mounts the ENTIRE grid — ~75k
 * elements at 437 columns × 170 rows, a ~1s main-thread stall every time the
 * tracker view opens. The seed keeps that first commit O(viewport); the real
 * measurement corrects the window one effect-pass later. Returns null only
 * when a mounted scroller measures clientWidth 0 (jsdom / display:none) —
 * callers still treat null as "render everything", which is what keeps the
 * existing component tests meaningful.
 *
 * Takes the scroller ELEMENT itself (via `useState`, not a `useRef`) — this
 * deliberately diverges from the Scheduler's `useVisibleRows`, which takes a
 * ref because its scroller never unmounts. The tracker's scroller DOES: an
 * empty generation / a critical-path / status / date filter that yields zero
 * columns makes `MatrixTable` early-return before the `.scroller` div, and when
 * columns come back a brand-new scroller element mounts. A ref object is
 * stable across that remount (same object, `.current` just changes), so an
 * effect keyed on `[scrollerRef]` never re-runs and the new element never gets
 * its scroll + ResizeObserver listeners. An element held in state changes
 * identity on remount, so keying the subscription effect on `[scroller]`
 * re-subscribes every time a new scroller mounts.
 *
 * Otherwise a direct transpose of useVisibleRows: rAF-coalesced passive
 * scroll + ResizeObserver subscriptions installed once per scroller element,
 * with a bail-out so scrolling WITHIN the buffered window never re-renders.
 * `win` itself only gets a new reference when at least one of the four
 * numbers (cols.start/end, rows.start/end) changes, but each AXIS keeps ITS
 * OWN previous range reference when only the other axis moved — a
 * vertical-only scroll must not hand out a new `cols` object, or the column
 * axis's own memoization (FloorRow/ZoneRow/MatrixCell keyed on the column
 * window) would think the columns changed and re-render every visible cell
 * on every vertical scroll tick.
 */
export function useVisibleGrid(
  scroller: HTMLElement | null,
  colCount: number,
  rowGeometry: IRowGeometry,
): IGridWindow | null {
  const [win, setWin] = useState<IGridWindow | null>(() => ({
    cols: seedColRange(colCount),
    rows: seedRowRange(rowGeometry.rows.length),
  }));

  const compute = useEffectEvent(() => {
    const el = scroller;
    if (!el) return;
    // A DETACHED scroller's measurements are meaningless — during the
    // empty-state teardown (0 columns unmounts the grid) a straggling
    // ResizeObserver/rAF callback can still fire against the removed element,
    // whose clientWidth of 0 would otherwise poison `win` to null ("render
    // everything"). The next non-empty render would then mount the FULL
    // unwindowed grid (~75k elements at 437 columns — a multi-second stall on
    // Clear after a 0-result filter). Ignore anything the DOM has let go of.
    if (!el.isConnected) return;
    const width = el.clientWidth;
    if (width === 0) {
      setWin((prev) => (prev === null ? prev : null));
      return;
    }
    const nextCols = visibleColRange(el.scrollLeft, width, colCount);
    const nextRows = visibleRowRange(el.scrollTop, el.clientHeight, rowGeometry.tops, rowGeometry.totalHeight);
    setWin((prev) => {
      const colsChanged = !prev || prev.cols.start !== nextCols.start || prev.cols.end !== nextCols.end;
      const rowsChanged = !prev || prev.rows.start !== nextRows.start || prev.rows.end !== nextRows.end;
      // Bail out (same `win` reference) only when BOTH axes are unchanged, but
      // when just ONE axis moved, keep the OTHER axis's range at its previous
      // reference (not a freshly-computed-but-value-identical object) — a
      // vertical-only scroll must not hand MatrixTable a new `cols` reference,
      // or its column-keyed memo (FloorRow/ZoneRow/MatrixCell) would think the
      // column window changed and re-render every visible cell on every
      // vertical scroll tick, defeating the column axis's own windowing.
      if (!colsChanged && !rowsChanged) return prev;
      return {
        cols: colsChanged ? nextCols : prev!.cols,
        rows: rowsChanged ? nextRows : prev!.rows,
      };
    });
  });

  useEffect(() => {
    const el = scroller;
    if (!el) return;

    let rafId: number | null = null;
    const schedule = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        compute();
      });
    };

    // Deliberate: seeds `win` for THIS scroller synchronously so there's no
    // one-frame "render everything" flash before the first real scroll/resize
    // event. useVisibleRows has the identical call and isn't flagged, only
    // because it reads `scrollerRef.current` — a ref read the rule's
    // reactivity tracking exempts — whereas this hook deliberately takes the
    // element via `useState` (see the doc comment above for why). The
    // "cascading render" the rule warns about is one bounded extra render
    // exactly when `scroller` changes identity (mount/remount), not a loop.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    compute();
    el.addEventListener('scroll', schedule, { passive: true });
    const ro = new ResizeObserver(schedule);
    ro.observe(el);

    return () => {
      el.removeEventListener('scroll', schedule);
      ro.disconnect();
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [scroller]);

  // Column count changed (filter/level/sort) or row geometry changed
  // (collapse/expand, floors rebuilt) — recompute without resubscribing.
  useEffect(() => {
    // While the scroller is UNMOUNTED (the 0-column empty state), there is
    // nothing to measure — RESEED a bounded window for the new shape instead,
    // so whatever `win` held when the grid emptied (stale range, or null from
    // a last-gasp zero-size measurement) can never dictate the size of the
    // remount when columns come back.
    if (!scroller) {
      const cols = seedColRange(colCount);
      const rows = seedRowRange(rowGeometry.rows.length);
      // eslint-disable-next-line react-hooks/set-state-in-effect -- bounded reseed, once per shape change while unmounted; see comment
      setWin((prev) =>
        prev &&
        prev.cols.start === cols.start && prev.cols.end === cols.end &&
        prev.rows.start === rows.start && prev.rows.end === rows.end
          ? prev
          : { cols, rows },
      );
      return;
    }
    // Same as the disable above: recomputing against the new colCount /
    // rowGeometry without resubscribing is this effect's entire purpose.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    compute();
  }, [scroller, colCount, rowGeometry]);

  return win;
}
